/**
 * Shared helper for Cloud Functions httpsCallable wrappers.
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '../../../libs/Firebase'
import { ApiResponse } from '../../types'

export const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback

/** Raw invocation: returns the callable's payload untouched. The wrappers below build on it. */
export async function invokeCallable<TReq, TRes>(name: string, req: TReq): Promise<TRes> {
  const callable = httpsCallable(functions, name)
  const result = await callable(req as Record<string, unknown>)
  return result.data as TRes
}

/** For callables whose whole payload is the data. */
export async function callCloudFunction<TReq, TRes>(
  name: string,
  req: TReq,
  fallbackMessage: string,
  fallbackData: TRes
): Promise<ApiResponse<TRes>> {
  try {
    return { data: await invokeCallable<TReq, TRes>(name, req), success: true }
  } catch (error: unknown) {
    return { data: fallbackData, success: false, error: getErrorMessage(error, fallbackMessage) }
  }
}

/**
 * For callables that answer with the shared `ApiResponse<T>` envelope: unwraps it instead of treating
 * the whole payload as the data. `fallbackData` is what the caller gets when the call itself fails.
 */
export async function callEnvelopeCloudFunction<TReq, TRes>(
  name: string,
  req: TReq,
  fallbackMessage: string,
  fallbackData: TRes
): Promise<ApiResponse<TRes>> {
  try {
    const response = await invokeCallable<TReq, ApiResponse<TRes>>(name, req)
    return { data: response.data, success: response.success, error: response.error }
  } catch (error: unknown) {
    return { data: fallbackData, success: false, error: getErrorMessage(error, fallbackMessage) }
  }
}
